import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import BasicInfoStep from '../../Component/v2/event/BasicInfoStep';
import RegistrationIdStep from '../../Component/v2/event/RegistrationIdStep';
import DynamicFieldsStep from '../../Component/v2/event/DynamicFieldsStep';
import CategoryConfigStep from '../../Component/v2/event/CategoryConfigStep';
import IntegrationKeysStep from '../../Component/v2/event/IntegrationKeysStep';
import TicketConfigStep from '../../Component/v2/event/TicketConfigStep';
import EmailConfigStep from '../../Component/v2/event/EmailConfigStep';
import EmailTemplatesStep from '../../Component/v2/event/EmailTemplatesStep';
import StepIndicator from '../../Component/v2/common/StepIndicator';
import { ChevronLeft, ChevronRight, Save, Check, ArrowLeft } from 'lucide-react';

const EventCreationPage = () => {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(0);
    const [loading, setLoading] = useState(!!eventId);
    const [stepErrors, setStepErrors] = useState({});
    const [formData, setFormData] = useState({
        eventName: '',
        eventStartDate: '',
        eventEndDate: '',
        eventType: 'free',
        generateRegistrationId: false,
        defaultIdCardBackground: null,
        participantFields: [
            {
                id: 'default-name',
                fieldName: 'name',
                fieldType: 'text',
                validation: 'required',
                placeholder: 'Enter full name',
                order: 0,
                isDefault: true,
            },
        ],
        integrationKeys: {
            organizerEmail: '',
        },
        emailConfig: {
            enabled: false,
            smtpHost: '',
            smtpPort: 587,
            smtpSecure: false,
            email: '',
            password: '',
            fromName: ''
        },
        emailTemplates: []
    });

    // Load event data if editing
    useEffect(() => {
        if (eventId) {
            fetchEventData(eventId);
        }
    }, [eventId]);

    const fetchEventData = async (id) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v2/events/${id}`);
            const data = await response.json();
            if (data.success) {
                // Format dates to YYYY-MM-DD for date inputs
                const formattedData = {
                    ...data.data,
                    eventStartDate: data.data.eventStartDate ? data.data.eventStartDate.split('T')[0] : '',
                    eventEndDate: data.data.eventEndDate ? data.data.eventEndDate.split('T')[0] : ''
                };
                setFormData(formattedData);
            }
        } catch (error) {
            console.error('Error fetching event:', error);
        } finally {
            setLoading(false);
        }
    };

    const getStepConfig = () => {
        const config = [
            { key: 'basic', title: 'Basic Information', description: 'Event details and type' },
            { key: 'regId', title: 'Registration ID', description: 'Configure registration ID' },
            { key: 'fields', title: 'Participant Fields', description: 'Dynamic registration fields' },
        ];

        if (formData.eventType === 'free') {
            config.push({ key: 'categories', title: 'Participant Categories', description: 'ID cards & amenities per category' });
        }

        config.push({ key: 'integration', title: 'Integration Keys', description: 'Payment & notification setup' });

        if (formData.eventType === 'paid' && formData.bookingType === 'single') {
            config.push({ key: 'tickets', title: 'Ticket Configuration', description: 'Setup tickets and pricing' });
        }

        config.push({ key: 'emailConfig', title: 'Email Configuration', description: 'SMTP settings and credentials' });
        config.push({ key: 'emailTemplates', title: 'Email Templates', description: 'Automated email templates' });

        return config;
    };

    const getSteps = () => {
        return getStepConfig().map((step, index) => ({
            id: index + 1,
            title: step.title,
            description: step.description,
            isCompleted: false,
            isActive: currentStep === index,
        }));
    };

    const steps = getSteps();

    const updateFormData = (data) => {
        setFormData((prev) => ({ ...prev, ...data }));
    };

    const clearError = (fieldId) => {
        setStepErrors((prev) => {
            const next = { ...prev };
            delete next[fieldId];
            return next;
        });
    };

    const validateCurrentStep = () => {
        const stepConfig = getStepConfig();
        const key = stepConfig[currentStep]?.key;
        const errors = {};

        if (key === 'basic') {
            if (!formData.eventName?.trim()) {
                errors['eventName'] = 'Event name is required';
            }
            if (!formData.eventStartDate) {
                errors['eventStartDate'] = 'Start date is required';
            }
            if (!formData.eventEndDate) {
                errors['eventEndDate'] = 'End date is required';
            } else if (formData.eventStartDate && formData.eventEndDate < formData.eventStartDate) {
                errors['eventEndDate'] = 'End date must be after start date';
            }
        }

        if (key === 'integration') {
            if (!formData.integrationKeys?.organizerEmail?.trim()) {
                errors['organizerEmail'] = 'Organizer email is required';
            }
            if (formData.eventType === 'paid') {
                if (!formData.integrationKeys?.razorpayKey?.trim()) {
                    errors['razorpayKey'] = 'Razorpay Key ID is required';
                }
                if (!formData.integrationKeys?.razorpaySecret?.trim()) {
                    errors['razorpaySecret'] = 'Razorpay Secret is required';
                }
            }
        }

        return errors;
    };

    const handleNext = () => {
        const errors = validateCurrentStep();
        if (Object.keys(errors).length > 0) {
            setStepErrors(errors);
            const firstErrorField = Object.keys(errors)[0];
            setTimeout(() => {
                const el = document.getElementById(firstErrorField);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    el.focus();
                }
            }, 50);
            return;
        }
        setStepErrors({});
        if (currentStep < steps.length - 1) {
            setCurrentStep((prev) => prev + 1);
        }
    };

    const handlePrevious = () => {
        setStepErrors({});
        if (currentStep > 0) {
            setCurrentStep((prev) => prev - 1);
        }
    };

    const handleSaveDraft = async () => {
        try {
            const response = await fetch(`/api/v2/events${eventId ? `/${eventId}` : ''}`, {
                method: eventId ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...formData, status: 'draft' }),
            });
            const data = await response.json();
            if (data.success) {
                alert('Draft saved successfully!');
                if (!eventId) {
                    navigate(`/v2/events/${data.data.id}/edit`);
                }
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Failed to save draft');
        }
    };

    const handleSubmit = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v2/events${eventId ? `/${eventId}` : ''}`, {
                method: eventId ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...formData, status: 'active' }),
            });
            const data = await response.json();
            if (data.success) {
                alert('Event created successfully!');
                navigate('/v2/events');
            } else {
                alert('Error: ' + (data.message || 'Failed to create event'));
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Failed to create event');
        }
    };

    const renderStep = () => {
        const stepConfig = getStepConfig();
        const key = stepConfig[currentStep]?.key;
        const props = { formData, updateFormData, errors: stepErrors, clearError };
        switch (key) {
            case 'basic':          return <BasicInfoStep {...props} />;
            case 'regId':          return <RegistrationIdStep {...props} />;
            case 'fields':         return <DynamicFieldsStep {...props} />;
            case 'categories':     return <CategoryConfigStep {...props} />;
            case 'integration':    return <IntegrationKeysStep {...props} />;
            case 'tickets':        return <TicketConfigStep {...props} />;
            case 'emailConfig':    return <EmailConfigStep {...props} />;
            case 'emailTemplates': return <EmailTemplatesStep {...props} />;
            default:               return null;
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 pb-24">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {/* Compact Header */}
                <div className="mb-6">
                    <button
                        onClick={() => navigate('/v2/events')}
                        className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-3"
                    >
                        <ArrowLeft className="w-4 h-4 mr-1" />
                        Back to Events
                    </button>
                    <h1 className="text-2xl font-bold text-gray-900">
                        {eventId ? 'Edit Event' : 'Create New Event'}
                    </h1>
                </div>

                {/* Compact Step Indicator */}
                <div className="mb-6">
                    <StepIndicator steps={steps} currentStep={currentStep} />
                </div>

                {/* Compact Form Content */}
                <div className="bg-white rounded-lg shadow-sm p-6 mb-4">
                    {renderStep()}
                </div>
            </div>

            {/* Sticky Navigation at Bottom */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <button
                            onClick={handlePrevious}
                            disabled={currentStep === 0}
                            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <ChevronLeft className="w-4 h-4 mr-1" />
                            Previous
                        </button>

                        <button
                            onClick={handleSaveDraft}
                            className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100"
                        >
                            <Save className="w-4 h-4 mr-1" />
                            Save Draft
                        </button>

                        {currentStep === steps.length - 1 ? (
                            <button
                                onClick={handleSubmit}
                                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
                            >
                                <Check className="w-4 h-4 mr-1" />
                                {eventId ? 'Update Event' : 'Create Event'}
                            </button>
                        ) : (
                            <button
                                onClick={handleNext}
                                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                            >
                                Next
                                <ChevronRight className="w-4 h-4 ml-1" />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EventCreationPage;